import type { MyInvite } from '@/services/invites';
import { possessive } from '@/lib/possessive';

/**
 * JOIN A TEAM — what `/join?invite=<id>` shows, in words. The page asks for
 * the signed-in person's own open invites and finds the one in the link; an
 * invite that isn't theirs, was cancelled or was already used is simply not
 * in that list, and the page says so plainly (it never says which).
 */

export type JoinView = { kind: 'invite'; invite: MyInvite } | { kind: 'gone' };

/** `?invite=12` → 12; anything else → null. */
export function parseInviteId(value: unknown): number | null {
  const n = typeof value === 'number' ? value : typeof value === 'string' && /^\d+$/.test(value.trim()) ? Number(value.trim()) : NaN;
  return Number.isSafeInteger(n) && n > 0 ? n : null;
}

/** The link's invite if it is still open for me; with no id in the link, my newest open one. */
export function joinView(invites: readonly MyInvite[], wanted: number | null): JoinView {
  const invite = wanted == null ? invites[0] : invites.find((i) => i.id === wanted);
  return invite ? { kind: 'invite', invite } : { kind: 'gone' };
}

const PRESET_NAMES: Record<string, string> = { owner: 'Owner', approver: 'Approver', reviewer: 'Reviewer', viewer: 'Viewer', agency: 'Agency' };

export const JOIN_WORDS = {
  goneTitle: 'This invite is no longer open',
  goneLine: 'If you already joined, the team is waiting in Dreamwell. If not, ask whoever invited you to send a new invite.',
  openDreamwell: 'Go to Dreamwell',
  notNow: 'Not now',
} as const;

export const joinTitle = (company: string) => `Join ${company || 'your team'}`;
export const joinButton = (company: string) => `Join ${company || 'the team'}`;

/**
 * "Soukaina invited you to join XERO on Dreamwell as a Reviewer." (a custom role is named by the team, not here). An
 * Agency invite says what the seat is for in the invite email's own sentence (authEmailService.ts teamInviteLead):
 * "Soukaina invited you to review drafts from Wavemaker’s creators for XERO on Dreamwell." (one possessive, as Home
 * and Profile say it). A name ending in s takes the apostrophe alone, "Influence Partners’ creators", as the email's
 * managedTeam/possessive.ts says it: `@/lib/possessive`, the
 * twin with no imports, since this file loads with the app's entry (routes/join.tsx reads `parseInviteId`).
 */
export function joinLine(invite: Pick<MyInvite, 'company' | 'invitedBy' | 'roleId' | 'agencyName'>): string {
  const who = invite.invitedBy?.trim() ? `${invite.invitedBy.trim()} invited you` : 'You’re invited';
  if (invite.roleId === 'agency' && invite.agencyName?.trim()) {
    return `${who} to review drafts from ${possessive(invite.agencyName)} creators for ${invite.company || 'a team'} on Dreamwell.`;
  }
  const role = PRESET_NAMES[invite.roleId];
  const as = role ? ` as ${/^[AEIOU]/.test(role) ? 'an' : 'a'} ${role}` : '';
  return `${who} to join ${invite.company || 'a team'} on Dreamwell${as}.`;
}
